import moment from 'moment'
import groupBy from 'lodash.groupby'
import debounce from 'lodash.debounce'
import { v4 as uuid } from 'uuid'
import CommonUtils from '../utils/commonUtils'
import { ChecklistService } from '../services/ChecklistService'

export const state = {
  checklists: [],
  allChecklists: [],
  checklistItems: [],
  selected: {},
  loading: false,
  printing: false,
  archiveMode: false,
}

export const mutations = {

  addCheckListItem(currentState, checklistItem) {
    currentState.checklist.checklistItems = currentState.checklist.checklistItems.concat(checklistItem)
  },
  setSelected(currentState, selected) {
    currentState.checklist.selected = selected
  },
  setArchiveMode(currentState, archiveMode) {
    currentState.checklist.archiveMode = archiveMode
  },
  setAllChecklists(currentState, allChecklists) {
    currentState.checklist.allChecklists = allChecklists
  },
  setChecklists(currentState, checklists) {
    currentState.checklist.checklists = checklists
  },
  setChecklistItems(currentState, checklistItems) {
    currentState.checklist.checklistItems = checklistItems
  },
  setChecklistsLoading(currentState, loading) {
    currentState.checklist.loading = loading
  },
  setItemDeleted(currentState, item) {
    currentState.checklist.checklistItems = currentState.checklist.checklistItems.filter(checklistItem => checklistItem._id !== item._id)
  },
  setPrinting(currentState, printing) {
    currentState.checklist.printing = printing
  },
}

export const getters = {

}

export const actions = {
  addChecklist({ commit, dispatch }, checklist) {
    return ChecklistService.upsertChecklist(checklist)
      .then(() => {
        dispatch('loadChecklists', 'last')
      })
  },
  addChecklistItem({ commit, rootState }, item) {
    const itemWithId = {
      ...item,
      _id: uuid(),
      clientid: rootState.authentication.userProfile.attributes.hublot_clientid[0],
    }
    commit('addCheckListItem', itemWithId)
    return ChecklistService.upsertChecklistItem(itemWithId)
  },
  copyItem({ commit }, item) {
    commit('addCheckListItem', { ...item, _id: uuid() })
    return ChecklistService.upsertChecklistItem(item)
  },
  setArchiveMode({ commit }, value) {
    commit('setArchiveMode', value)
  },
  setSelected({ commit, dispatch }, selected) {
    commit('setSelected', selected)
    commit('setChecklistsLoading', true)

    return ChecklistService.getChecklistItemById(selected._id)
      .then((checklistItems) => Promise.all(
        checklistItems.map(item => ChecklistService.getChecklistItemEmployeesById(item._id)
          .then(employees => ({ ...item, employeesItem: employees }))),
      ))
      .then((itemsWithEmployees) => {
        // 1. Tri par ordre actuel
        const sorted = [...itemsWithEmployees].sort((a, b) => a.order - b.order)

        // 2. Réindexation de l'ordre
        const reindexed = sorted.map((item, index) => ({
          ...item,
          order: index,
        }))

        // 3. Commit
        commit('setChecklistItems', reindexed)
        commit('setChecklistsLoading', false)

        // 4. Dispatch
        dispatch('updateChecklistItems', reindexed)
      })
  },
  deleteItem({ commit }, item) {
    return ChecklistService.upsertChecklistItem({ ...item, deleted: true })
      .then(() => {
        commit('setItemDeleted', item)
      })
  },
  updateEmployeeList({ commit, rootState }, checklistItem) {
    return Promise.all([
      ...checklistItem.employeesItem.map(employee => ChecklistService.upsertChecklistItemEmployees(employee)),
    ]).then(() => {
      commit('setChecklistItems', rootState.checklist.checklistItems.map(checklistItemBase => (checklistItemBase._id === checklistItem._id ? checklistItem : checklistItemBase)))
    })
  },
  updateItem({ commit, rootState }, item) {
    return ChecklistService.upsertChecklistItem(item)
      .then(() => {
      // Need to reload list with current item
        commit('setChecklistItems', rootState.checklist.checklistItems.map(checklistItem => (checklistItem._id === item._id ? item : checklistItem)))
      })
  },
  updateChecklist({ commit, dispatch }, checklist) {
    return ChecklistService.upsertChecklist(checklist)
      .then(() => {
        dispatch('loadChecklists', (checklist.deleted === true || checklist.archive !== null) ? 'last' : checklist._id)
      })
  },
  async updateChecklistItems({ commit }, items) {
    const now = moment().format('YYYY-MM-DD HH:mm:ss')

    const updatedItems = items.map(item => ({
      ...item,
      lastUpdate: now,
    }))

    try {
      await Promise.all(
        updatedItems.map(item => ChecklistService.upsertChecklistItem(item)),
      )

      commit('setChecklistItems', updatedItems)
    } catch (error) {
      console.error('Erreur lors de la mise à jour des checklist items', error)
    }
  },
  loadChecklists({ commit, rootState, dispatch }, selected = null) {
    commit('setChecklistsLoading', true)
    return ChecklistService.getChecklists()
      .then(checklists => {
        if (checklists.filter(checklist => checklist.archive === null).length === 0) {
          dispatch('addChecklist', {
            _id: uuid(), title: '(Sans titre)', dateCreation: moment().format('YYYY-MM-DD HH:mm:ss'), archive: null,
          })
          commit('setChecklistsLoading', false)
          return
        }
        const checklistsArchiveLength = checklists.filter(checklist => checklist.archive !== null).length > 0
        const currentChecklists = checklists.filter(checklist => (rootState.checklist.archiveMode && checklistsArchiveLength ? checklist.archive !== null : checklist.archive === null)).sort((a, b) => new Date(a.dateCreation) - new Date(b.dateCreation))
        if (!checklistsArchiveLength) {
          dispatch('setArchiveMode', false)
        }
        if (selected !== null) {
          if (selected === 'last') {
            dispatch('setSelected', currentChecklists[currentChecklists.length - 1])
          } else if (selected === 'first') {
            dispatch('setSelected', currentChecklists[0])
          } else {
            dispatch('setSelected', currentChecklists.filter(checklist => checklist._id === selected)[0])
          }
        } else {
          dispatch('setSelected', currentChecklists[0])
        }
        commit('setAllChecklists', checklists.sort((a, b) => new Date(a.dateCreation) - new Date(b.dateCreation)))
        commit('setChecklists', currentChecklists)
        commit('setChecklistsLoading', false)
      })
  },
  printChecklist({ commit, rootState, dispatch }, checklist) {
    commit('setPrinting', true)
    return ChecklistService.downloadChecklist(checklist)
      .then(() => {
        dispatch('updateChecklistItems', rootState.checklist.checklistItems.map(checklistItem => ({ ...checklistItem, printed: moment().format('YYYY-MM-DD HH:mm:ss') })))
        commit('setPrinting', false)
      })
  },
}

export default {
  actions, mutations, state, getters,
}
